import { memo, type ReactNode, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled, { css, keyframes } from 'styled-components';

import type { ChatMessage, UiRender } from '@paradigm/terminal-core';

import { MONO_BOX } from '../../../styles/mono-box';
import { useTerminalConfig } from '../../../context/config-context';
import { AgentRenderer } from '../../generative/AgentRenderer';
import ApprovalCard from '../ApprovalCard';

import { type AgentSpec, tryExtractInlineAgentSpec } from './inline-agent-spec';
import { makeModelMentionRewriter } from './model-mentions';

// GFM enables real `<table>` rendering for pipe-delimited markdown tables
// (plus strikethrough + autolinks). Without it the agent's tables collapse
// into a one-line wall of `|`-separated text.
const MARKDOWN_PLUGINS = [remarkGfm];

// Wrap `<table>` in a rounded container so border-radius reads cleanly —
// `border-collapse: collapse` (set in MarkdownContent below) prevents the
// table element itself from honoring corner radii.
const MARKDOWN_COMPONENTS = {
  table: ({ children }: { readonly children?: ReactNode }) => (
    // `tabIndex={0}` makes the container keyboard-focusable so the horizontal
    // overflow can be scrolled with arrow keys; `role="region"` + the label
    // gives screen readers a discoverable landmark.
    <MarkdownTableContainer role="region" aria-label="Table" tabIndex={0}>
      <table>{children}</table>
    </MarkdownTableContainer>
  ),
  // Constrain agent-emitted images: without this a `![](...)` renders as a
  // raw unbounded <img> that can blow out the chat column. URL scheme safety
  // is react-markdown's default urlTransform (http/https only).
  img: ({ src, alt }: { readonly src?: string; readonly alt?: string }) => (
    <MarkdownImg
      src={src}
      alt={alt ?? ''}
      loading="lazy"
      referrerPolicy="no-referrer"
    />
  ),
};

export const MessageBubble = memo(function MessageBubble({
  message,
  onApprovalRespond,
  onUiEvent,
}: {
  readonly message: ChatMessage;
  readonly onApprovalRespond: (approvalId: string, approved: boolean) => void;
  readonly onUiEvent: (params: {
    readonly surfaceId: string;
    readonly componentId?: string;
    readonly event: string;
    readonly data?: Record<string, unknown>;
  }) => void;
}) {
  const config = useTerminalConfig();

  // Generative UI is gated on `features.generativeUi` AND a host-supplied
  // component catalog. When either is missing, structured renders fall back to
  // a "[UI disabled]" placeholder (see UiRenderer / the agentSpec branch).
  const generativeUiEnabled =
    config.features?.generativeUi === true &&
    config.capabilities?.components != null;

  // Rewrite model-id mentions in assistant text to the same native friendly
  // names the picker uses, so chat text and the picker never disagree.
  const renderAssistantText = useMemo(() => makeModelMentionRewriter(), []);

  // Chat widgets (`WIDGET_REGISTRY`) are a host coupling with no package
  // capability seam yet, so widget messages render a placeholder.
  // INJECTION POINT (widgets): when a `capabilities.widgets` registry is
  // added to `TerminalConfig`, resolve `message.widgetType` against it here
  // and render `<WidgetComponent data={message.widgetData} />` inside a host
  // error boundary.
  // TODO(host-injection): add `capabilities.widgets` to `TerminalConfig` and
  // wire it in; today every widget message falls back to the placeholder.
  const hasWidget = message.widgetType != null && message.widgetData != null;
  // Resolve a host-injected widget component for `widgetType`, if any.
  const WidgetComponent =
    hasWidget && message.widgetType != null
      ? (config.capabilities?.widgets?.[message.widgetType] ??
        config.capabilities?.widgets?.[`paradex-${message.widgetType}`])
      : undefined;

  // Detect inline AgentSpec JSON in plain-text assistant messages.
  // The LLM sometimes streams the component-tree JSON as text deltas
  // instead of a structured widget frame — parse it at render time so
  // the chat shows components rather than raw JSON.
  const inlineSpec = useMemo(() => {
    if (
      message.role === 'assistant' &&
      message.agentSpec == null &&
      message.ui == null &&
      message.content.includes('"layout"')
    ) {
      // Run on streaming content too — partial JSON fails parse silently
      // (parseable only once the matching close brace has streamed in),
      // so we get the table the moment the tree is complete instead of
      // waiting for the whole message to finalize.
      return tryExtractInlineAgentSpec(message.content);
    }
    return null;
  }, [message.role, message.agentSpec, message.ui, message.content]);

  // Generative UI render (new `ui.render` protocol). `full-terminal` placement
  // is rendered by the overlay in ChatInterface, so swallow here.
  if (message.ui != null) {
    if (message.ui.placement === 'full-terminal') {
      return (
        <BubbleRow $role={message.role} data-msg-ts={message.timestamp}>
          <FullTerminalStub>Rendered in terminal viewport ↑</FullTerminalStub>
        </BubbleRow>
      );
    }
    return (
      <BubbleRow $role={message.role} data-msg-ts={message.timestamp}>
        <UiRenderer
          ui={message.ui}
          surfaceId={message.id}
          sendUiEvent={onUiEvent}
        />
        <BubbleTime>
          {new Date(message.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </BubbleTime>
      </BubbleRow>
    );
  }

  // Agent spec — render structured component tree (explicit or inline-detected)
  if (message.agentSpec != null || inlineSpec != null) {
    const spec = message.agentSpec ?? inlineSpec!.spec;
    const preamble = message.agentSpec != null ? null : inlineSpec!.preamble;
    return (
      <BubbleRow $role={message.role} data-msg-ts={message.timestamp}>
        {preamble != null && preamble.length > 0 && (
          <Bubble $role={message.role}>
            <MarkdownContent>
              <ReactMarkdown
                remarkPlugins={MARKDOWN_PLUGINS}
                components={MARKDOWN_COMPONENTS}
              >
                {preamble}
              </ReactMarkdown>
            </MarkdownContent>
          </Bubble>
        )}
        <AgentSpecWrap>
          {generativeUiEnabled ? (
            <AgentRenderer spec={spec} />
          ) : (
            <AgentSpecPlaceholder enabled={generativeUiEnabled} spec={spec} />
          )}
        </AgentSpecWrap>
        <BubbleTime>
          {new Date(message.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </BubbleTime>
      </BubbleRow>
    );
  }

  // Approval request — render as interactive card
  if (message.approval != null) {
    return (
      <BubbleRow $role={message.role} data-msg-ts={message.timestamp}>
        <ApprovalCard
          approval={message.approval}
          onRespond={onApprovalRespond}
        />
        <BubbleTime>
          {new Date(message.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </BubbleTime>
      </BubbleRow>
    );
  }

  return (
    <BubbleRow $role={message.role} data-msg-ts={message.timestamp}>
      <Bubble $role={message.role}>
        {message.attachments != null && message.attachments.length > 0 && (
          <AttachmentStrip>
            {/* Attachments are immutable per message (never reordered or
                edited), so a positional key is stable here. */}
            {message.attachments.map((a, i) =>
              a.previewUrl != null ? (
                <AttachmentThumb
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${message.id}-att-${i}`}
                  src={a.previewUrl}
                  alt={a.fileName ?? 'attached image'}
                />
              ) : (
                // Non-image files and history/echo frames carry no thumbnail
                // — show a chip so the message never reads as empty.
                // eslint-disable-next-line react/no-array-index-key
                <AttachmentChip key={`${message.id}-att-${i}`}>
                  {a.fileName ?? (a.type === 'file' ? 'file' : 'image')}
                </AttachmentChip>
              ),
            )}
          </AttachmentStrip>
        )}
        {hasWidget ? (
          WidgetComponent != null ? (
            <WidgetComponent data={message.widgetData ?? {}} />
          ) : (
            <WidgetPlaceholder widgetType={message.widgetType} />
          )
        ) : message.role === 'assistant' ? (
          <MarkdownContent>
            <ReactMarkdown
              remarkPlugins={MARKDOWN_PLUGINS}
              components={MARKDOWN_COMPONENTS}
            >
              {renderAssistantText(message.content)}
            </ReactMarkdown>
          </MarkdownContent>
        ) : (
          <BubbleContent>{message.content}</BubbleContent>
        )}
        {message.isStreaming === true && <StreamingDots>...</StreamingDots>}
      </Bubble>
      <BubbleTime>
        {new Date(message.timestamp).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        })}
      </BubbleTime>
    </BubbleRow>
  );
});

/**
 * Placeholder for a structured agent spec when generative UI is disabled (or
 * no host catalog is wired). When enabled this is where the host renderer
 * would mount; the package itself never imports a renderer.
 */
function AgentSpecPlaceholder({
  enabled,
  spec,
}: {
  readonly enabled: boolean;
  readonly spec: AgentSpec;
}) {
  void spec;
  if (enabled) {
    // INJECTION POINT (generative-UI): a host that enabled generativeUi but
    // hasn't wired a renderer through this component lands here. Hosts should
    // render their catalog renderer against `spec`.
    return <DisabledUiStub>[UI render pending host renderer]</DisabledUiStub>;
  }
  return <DisabledUiStub>[UI disabled]</DisabledUiStub>;
}

/** Placeholder for a widget message when no host widget registry is wired. */
function WidgetPlaceholder({
  widgetType,
}: {
  readonly widgetType: string | undefined;
}) {
  return (
    <DisabledUiStub>
      [Widget disabled{widgetType != null ? `: ${widgetType}` : ''}]
    </DisabledUiStub>
  );
}

/**
 * Dispatches a `UiRender` payload. In the package build both surfaces are
 * gated:
 * - `catalog` → host catalog renderer (generative-UI injection point).
 * - `mcp-app` → sandboxed iframe view (host `McpAppView` injection point).
 *
 * Neither renderer is imported here; when the corresponding capability is
 * absent we render a placeholder. Events would be forwarded back to the agent
 * via `sendUiEvent` once a renderer is wired.
 */
export function UiRenderer({
  ui,
  surfaceId,
  sendUiEvent,
}: {
  readonly ui: UiRender;
  readonly surfaceId: string;
  readonly sendUiEvent: (params: {
    readonly surfaceId: string;
    readonly componentId?: string;
    readonly event: string;
    readonly data?: Record<string, unknown>;
  }) => void;
}) {
  const config = useTerminalConfig();
  // Keep the signature stable for the future renderer; unused until injected.
  void surfaceId;
  void sendUiEvent;

  if (ui.mode === 'catalog') {
    const generativeUiEnabled =
      config.features?.generativeUi === true &&
      config.capabilities?.components != null;
    if (generativeUiEnabled) {
      return <AgentRenderer spec={ui.spec} />;
    }
    return <DisabledUiStub>[UI disabled]</DisabledUiStub>;
  }

  // mode === 'mcp-app'
  // INJECTION POINT (mcp-app): mount the host `McpAppView` (sandboxed iframe)
  // for `ui.resourceUri` / `ui.html` here, gated on `features.mcpApps`. The
  // package ships no iframe view.
  return (
    <DisabledUiStub>
      [MCP app disabled{ui.title != null ? `: ${ui.title}` : ''}]
    </DisabledUiStub>
  );
}

const BubbleRow = styled.div<{ $role: string }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
`;

const Bubble = styled.div<{ $role: string }>`
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.5;
  word-break: break-word;
  color: var(--text-primary);

  ${({ $role }) =>
    $role === 'user'
      ? css`
          max-width: 85%;
          white-space: pre-wrap;
          background: var(--button-color);
          padding: 8px 12px;
          border-radius: 12px;
          border-bottom-right-radius: 4px;
        `
      : css`
          width: 100%;
          background: none;
          padding: 0;
        `}
`;

const BubbleContent = styled.span``;

const AttachmentStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const AttachmentThumb = styled.img`
  max-width: 160px;
  max-height: 120px;
  border-radius: 8px;
  object-fit: cover;
`;

const AttachmentChip = styled.span`
  font-size: 0.6875rem;
  color: var(--text-secondary);
  padding: 3px 8px;
  border: 1px dashed var(--border-color);
  border-radius: 6px;
`;

const MarkdownImg = styled.img`
  display: block;
  max-width: 100%;
  max-height: 320px;
  border-radius: 6px;
  margin: 0.4em 0;
`;

/** Full-width wrapper so the catalog renderer (especially charts with
 *  ResponsiveContainer) gets a measurable width from the chat column. */
const AgentSpecWrap = styled.div`
  width: 100%;
  min-width: 0;
  overflow: hidden;
`;

const DisabledUiStub = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  padding: 8px 12px;
  border: 1px dashed var(--border-color);
  border-radius: 6px;
`;

// Paradex-style container for markdown tables: mirrors the mono/dark-box
// treatment used for fenced code (`MONO_BOX`, shared with ApprovalCard's
// command preview) so agent-text tables and code output read as the same
// "raw terminal data" style.
const MarkdownTableContainer = styled.div`
  ${MONO_BOX}
  font-size: 0.75rem;
  line-height: 1.45;
  margin: 0.5em 0;
  padding: 8px 10px;

  & > table {
    width: 100%;
    border-collapse: collapse;
    font-size: inherit;
  }

  & th,
  & td {
    padding: 4px 12px 4px 0;
    text-align: left;
    vertical-align: top;
  }

  & th {
    color: var(--text-label);
    font-weight: 400;
    /* Headers stay one line for legibility; cell prose wraps freely. */
    white-space: nowrap;
    border-bottom: 1px solid var(--border-color);
  }

  & td {
    color: var(--text-primary);
  }
`;

const MarkdownContent = styled.div`
  & > :first-child {
    margin-top: 0;
  }
  & > :last-child {
    margin-bottom: 0;
  }
  /* Every element the markdown renderer emits gets its color EXPLICITLY
     from the theme variables — never by inheritance. The package renders
     into host pages whose globals routinely style these tags (pre/code
     syntax themes, prose stylesheets), and a direct host rule beats any
     inherited color; a light-theme host code rule once rendered fenced
     yaml near-black on the dark mono box. Class-scoped rules here always
     out-specify host element selectors. */
  p,
  li {
    color: var(--text-primary);
  }
  p {
    margin: 0.4em 0;
  }
  ul,
  ol {
    margin: 0.4em 0;
    padding-left: 1.2em;
  }
  li {
    margin: 0.15em 0;
  }
  code {
    font-family: var(--font-mono, monospace);
    font-size: 0.85em;
    color: var(--text-primary);
    background: rgba(var(--color-lightest), 0.1);
    padding: 0.1em 0.3em;
    border-radius: 3px;
  }
  pre {
    ${MONO_BOX}
    font-size: 0.75rem;
    line-height: 1.45;
    margin: 0.5em 0;
    padding: 8px 10px;
  }
  pre code {
    color: var(--text-primary);
    background: none;
    padding: 0;
    border-radius: 0;
    font-size: inherit;
  }
  a {
    color: var(--accent-color, inherit);
    text-decoration: underline;
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0.6em 0 0.3em;
    color: var(--text-primary);
    font-weight: 500;
    line-height: 1.3;
  }
  blockquote {
    margin: 0.4em 0;
    padding-left: 10px;
    border-left: 2px solid var(--border-color);
    color: var(--text-secondary);
  }
`;

const pulse = keyframes`
  0%, 80%, 100% { opacity: 0.3; }
  40% { opacity: 1; }
`;

const StreamingDots = styled.span`
  animation: ${pulse} 1.2s ease-in-out infinite;
  margin-left: 2px;
`;

const BubbleTime = styled.span`
  font-size: 0.5625rem;
  color: var(--text-secondary);
  opacity: 0.6;
  margin-top: 2px;
  padding: 0 4px;
`;

const FullTerminalStub = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  padding: 8px 12px;
  border: 1px dashed var(--border-color);
  border-radius: 6px;
`;
